import { faFilterSlash } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GroupedHistogram } from '@repo/videa-components/src/common-charts/index';
import { cn } from '@repo/videa-components/src/lib/utils';
import { chartColors } from 'node_modules/@repo/videa-components/src/common-charts/themes/colors';

import { usePreferences } from '@/react-query/usePreferences';

import { TrendIndicator } from '../components/TrendIndicator';

export const BenchmarkingDistributionChart = ({
  rate,
  previousRate,
  lowerBenchmark,
  upperBenchmark,
  distribution,
  isLoading,
  isConversionRate,
}: {
  unitType?: 'DSO' | 'Provider' | 'Practice' | 'Region';
  rate?: number;
  previousRate?: number;
  lowerBenchmark?: number;
  upperBenchmark?: number;
  distribution?: Record<number, number>;
  isLoading?: boolean;
  isConversionRate?: boolean;
}) => {
  const { data: prefs } = usePreferences();
  const isEngagementMetric = prefs?.enableInsightsEngagementMetric;

  const plotLines: {
    value?: number;
    dashStyle?: 'Dash' | 'Dot' | 'Solid';
  }[] = [
    {
      value: rate,
      dashStyle: 'Dash',
    },
  ];

  const lowerBenchmarkWidth =
    isConversionRate && lowerBenchmark
      ? Math.min(lowerBenchmark * 2, 1)
      : lowerBenchmark;
  const upperBenchmarkWidth =
    isConversionRate && upperBenchmark
      ? Math.min(upperBenchmark * 2, 1)
      : upperBenchmark;

  const buckets = [
    {
      label: isConversionRate ? 'Low' : '',
      color: 'vai-bg-chart-tint-1',
      textColor: 'vai-text-chart-shade-1',
      width: lowerBenchmarkWidth ? lowerBenchmarkWidth : 0,
    },
    {
      label: isConversionRate ? 'Medium' : '',
      color: 'vai-bg-chart-normal-1',
      textColor: 'vai-text-white',
      width: (upperBenchmarkWidth ?? 1) - (lowerBenchmarkWidth ?? 0),
    },
    {
      label: isConversionRate ? 'High' : '',
      color: 'vai-bg-chart-shade-1',
      textColor: 'vai-text-white',
      width: upperBenchmarkWidth ? 1 - upperBenchmarkWidth : 1,
    },
  ];

  const rateChange =
    rate && previousRate
      ? ((rate - previousRate) / previousRate) * 100
      : undefined;

  return (
    <div>
      {rate !== undefined && distribution !== undefined && !isLoading && (
        <div className="vai-flex vai-flex-row vai-w-full vai-gap-1 vai-pt-2">
          <div className="vai-flex vai-flex-col vai-justify-center vai-w-[60px] vai-gap-2">
            <span className="vai-typography-h2 vai-font-semi-bold vai-text-left">
              {Math.round(rate * 100)}%
            </span>
            {rateChange !== undefined && (
              <TrendIndicator value={rateChange} suffix="%" />
            )}
          </div>
          <div className="vai-flex vai-w-[calc(100%-64px)]">
            <GroupedHistogram
              data={[
                {
                  name: 'Provider Rates Distribution',
                  data: Object.values(distribution || {}),
                },
              ]}
              chartOptions={{
                height: isEngagementMetric ? 190 : 135,
                colors: [chartColors['chart-tint']?.['viz-empty'] ?? '#000000'],
                lineWidth: 0,
                yAxisMax:
                  Math.max(
                    ...Object.values(distribution || {}).filter(
                      (value) => !isNaN(value),
                    ),
                  ) + 1,
                tooltipFormatter: function (this: any) {
                  if (isConversionRate) {
                    if (this.x >= 19) {
                      return `${this.x * 2.5}%+ : ${this.y} providers`;
                    }
                    return `${this.x * 2.5}-${this.x * 2.5 + 2.5}%: ${this.y} providers`;
                  }
                  return `${this.x * 10}-${this.x * 10 + 10}%: ${this.y} providers`;
                },
                xAxisFormatter: function (this: any) {
                  if (isConversionRate) {
                    if (this.isLast) {
                      return `${this.value * 2.5}%+`;
                    }
                    return `${this.value * 2.5}%`;
                  }
                  return `${this.value * 10}%`;
                },
                plotLines: plotLines,
              }}
              buckets={buckets}
              size="small"
              numberOfHistogramBuckets={isConversionRate ? 20 : 10}
              clippedPercentage={isConversionRate ? 0.5 : undefined}
            />
          </div>
        </div>
      )}
      {!isLoading && (rate === undefined || distribution === undefined) && (
        <div className="vai-flex vai-gap-1 vai-h-full vai-w-full vai-flex-col vai-items-center vai-justify-center vai-flex-1">
          <FontAwesomeIcon
            icon={faFilterSlash}
            className="vai-h-10 vai-w-10 vai-text-zinc-300 vai-p-2"
          />
          <span className="vai-text-lg vai-text-muted-foreground">No data</span>
          <span className="vai-typography-muted">
            Adjust your filters and try again
          </span>
        </div>
      )}
      {isLoading && (
        <div className="vai-flex vai-h-full vai-w-full vai-items-center vai-justify-center vai-mt-4">
          <img
            src={
              isConversionRate
                ? '/assets/images/insights/conversionRateLoading.png'
                : '/assets/images/insights/benchmarkLoading.png'
            }
            alt="loading"
            className={cn(`vai-w-full vai-opacity-15`, {
              'vai-h-[200px]': isEngagementMetric,
              'vai-h-[120px]': !isEngagementMetric,
            })}
          />
        </div>
      )}
    </div>
  );
};
